/**
 * Client for the PUBLIC (unauthenticated) waiver endpoints (tv-k9b.11).
 *
 * The waiver link the customer is texted (/customer-waiver/:waiverId) renders
 * with NO staff login, so the page loads and signs its one waiver through these
 * public routes, never the staff store (whose /api/bootstrap answers 401 to a
 * logged-out visitor). Mirrors api/publicDeposit.ts.
 *
 * One clock (tv-2na.10.5): the sign sends the sim clock as `nowIso`; the server
 * honours it only in a demo/dev deployment.
 */
import { useSimulationStore } from '@/state/simulationStore';
import { WAIVER_DOCUMENT } from '@/domain/waiverDocument';
import type { SessionLength, WaiverAnswers } from '@/domain/types';

/** What the page shows: the client's first name and their appointment, nothing more. */
export interface PublicWaiverBundle {
  waiver: { id: string; status: string; signedAt: string | null };
  /** open = can be signed; signed = done; expired = the booking is no longer live. */
  linkState: 'open' | 'signed' | 'expired';
  firstName: string | null;
  booking: { start: string | null; sessionLength: SessionLength; durationMin: number } | null;
  artistName: string | null;
  studioName: string | null;
}

/** Load one waiver link. Null for a missing waiver (404); throws only on a
 *  network / server error so the page can say "try again". */
export async function fetchPublicWaiver(id: string): Promise<PublicWaiverBundle | null> {
  const res = await fetch(`/api/public/waiver/${encodeURIComponent(id)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`public-waiver ${res.status}`);
  return (await res.json()) as PublicWaiverBundle;
}

export type SignPublicWaiverResult =
  | { ok: true; alreadySigned: boolean }
  | { ok: false; status: number; error: string };

/** Sign the waiver with the customer's answers. Never throws. */
export async function signPublicWaiver(id: string, answers: WaiverAnswers): Promise<SignPublicWaiverResult> {
  let res: Response;
  try {
    res = await fetch(`/api/public/waiver/${encodeURIComponent(id)}/sign`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        answers,
        documentVersion: WAIVER_DOCUMENT.version,
        nowIso: useSimulationStore.getState().clockIso,
      }),
    });
  } catch {
    return { ok: false, status: 0, error: 'network' };
  }
  if (res.ok) {
    const body = (await res.json().catch(() => ({}))) as { alreadySigned?: unknown };
    return { ok: true, alreadySigned: body.alreadySigned === true };
  }
  const body = (await res.json().catch(() => undefined)) as { error?: string } | undefined;
  return { ok: false, status: res.status, error: body?.error ?? `http-${res.status}` };
}
